import { Eye, Layers, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import forgeService from '../../services/forgeService';
import notify from '../../lib/notify';
import { extractApiErrorMessage } from '../../lib/apiError';
import { Button } from '../Common/button';
import { Badge } from '../Common/badge';
import Modal from '../Common/Modal';
import ConfirmationModal from '../Common/ConfirmationModal';
import TableSkeleton from '../Common/TableSkeleton';
import { ProgressStepper, ProgressStep } from '../Common/ProgressStepper';
import {
  CreateForgeRequest,
  Forge,
  ForgeClient,
  UpdateForgeRequest,
} from '../../types';

// Mirrors backend CreateForgeRequest.name validation
// (backend/app/schemas/deployment/forge.py): lowercase letters, digits and
// hyphens, must start and end with alphanumeric, length 2–63.
const FORGE_NAME_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const isValidForgeName = (n: string) =>
  n.length >= 2 && n.length <= 63 && FORGE_NAME_REGEX.test(n);

type CreateFormState = {
  client_id: string;
  name: string;
  display_name: string;
  description: string;
  validation_strategy: 'strict' | 'permissive';
  auto_rollback_enabled: boolean;
  registry_storage_size: string;
};

const defaultCreate = (): CreateFormState => ({
  client_id: '',
  name: '',
  display_name: '',
  description: '',
  validation_strategy: 'strict',
  auto_rollback_enabled: true,
  registry_storage_size: '20Gi',
});

type EditFormState = {
  display_name: string;
  auto_rollback_enabled: boolean;
  validation_strategy: 'strict' | 'permissive';
};

const getStatusBadgeVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'active':
      return 'default';
    case 'creating':
    case 'deploying':
    case 'updating':
      return 'secondary';
    case 'error':
    case 'deleting':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

export const ForgesTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [forges, setForges] = useState<Forge[]>([]);
  const [clients, setClients] = useState<ForgeClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(defaultCreate());
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [editForge, setEditForge] = useState<Forge | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({
    display_name: '',
    auto_rollback_enabled: true,
    validation_strategy: 'strict',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteForge, setDeleteForge] = useState<Forge | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [createStep, setCreateStep] = useState<number>(-1);
  const [hasCreateError, setHasCreateError] = useState(false);
  const createStepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const createSteps: ProgressStep[] = [
    { id: 'validate', label: t('forgeDashboard.forges.progress.validating', 'Validating') },
    { id: 'provision', label: t('forgeDashboard.forges.progress.provisioning', 'Provisioning') },
    { id: 'deploy', label: t('forgeDashboard.forges.progress.deploying', 'Deploying') },
    { id: 'finalize', label: t('forgeDashboard.forges.progress.finalizing', 'Finalizing') },
    { id: 'done', label: t('forgeDashboard.forges.progress.done', 'Done') },
  ];

  const isCreating = createStep >= 0;

  const nameTrimmed = createForm.name.trim();
  const nameInvalid = nameTrimmed.length > 0 && !isValidForgeName(nameTrimmed);
  const canSubmitCreate =
    !!createForm.client_id &&
    isValidForgeName(nameTrimmed) &&
    !!createForm.display_name.trim();

  useEffect(() => {
    return () => {
      if (createStepIntervalRef.current) clearInterval(createStepIntervalRef.current);
    };
  }, []);

  // Advance through the first N-1 steps on a timer; the last step is set
  // explicitly when the network call succeeds.
  const startCreateStepSimulation = () => {
    setCreateStep(0);
    let current = 0;
    if (createStepIntervalRef.current) clearInterval(createStepIntervalRef.current);
    createStepIntervalRef.current = setInterval(() => {
      current += 1;
      if (current >= createSteps.length - 1) {
        if (createStepIntervalRef.current) {
          clearInterval(createStepIntervalRef.current);
          createStepIntervalRef.current = null;
        }
        return;
      }
      setCreateStep(current);
    }, 800);
  };

  const stopCreateStepSimulation = () => {
    if (createStepIntervalRef.current) {
      clearInterval(createStepIntervalRef.current);
      createStepIntervalRef.current = null;
    }
  };

  const clientById = useMemo(() => {
    const m = new Map<string, ForgeClient>();
    clients.forEach((c) => m.set(c.client_id, c));
    return m;
  }, [clients]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, c] = await Promise.all([forgeService.listForges(), forgeService.listClients()]);
      setForges(f);
      setClients(c);
    } catch (err) {
      console.error('Failed to load forges/clients', err);
      setError(t('forgeDashboard.forges.loadError', 'Failed to load forges'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (f: Forge) => {
    setEditForge(f);
    setEditForm({
      display_name: f.display_name || '',
      auto_rollback_enabled: true,
      validation_strategy: 'strict',
    });
  };

  const handleCreate = async () => {
    if (!canSubmitCreate) return;
    setCreateSubmitting(true);
    setHasCreateError(false);
    startCreateStepSimulation();
    try {
      const payload: CreateForgeRequest = {
        name: nameTrimmed,
        display_name: createForm.display_name.trim(),
        description: createForm.description,
        validation_strategy: createForm.validation_strategy,
        auto_rollback_enabled: createForm.auto_rollback_enabled,
        registry_storage_size: createForm.registry_storage_size,
      };
      await forgeService.createForge(createForm.client_id, payload);
      stopCreateStepSimulation();
      setCreateStep(createSteps.length - 1);
      await new Promise((r) => setTimeout(r, 500));
      notify.success(t('forgeDashboard.forges.createSuccess', 'Forge created'));
      setCreateOpen(false);
      setCreateForm(defaultCreate());
      fetchAll();
    } catch (err) {
      stopCreateStepSimulation();
      setHasCreateError(true);
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.forges.createError', 'Failed to create forge')));
      await new Promise((r) => setTimeout(r, 1200));
    } finally {
      setCreateSubmitting(false);
      setCreateStep(-1);
      setHasCreateError(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForge) return;
    setEditSubmitting(true);
    try {
      const payload: UpdateForgeRequest = {
        display_name: editForm.display_name || null,
        auto_rollback_enabled: editForm.auto_rollback_enabled,
        validation_strategy: editForm.validation_strategy,
      };
      await forgeService.updateForge(editForge.client_id, editForge.forge_id, payload);
      notify.success(t('forgeDashboard.forges.updateSuccess', 'Forge updated'));
      setEditForge(null);
      fetchAll();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.forges.updateError', 'Failed to update forge')));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteForge) return;
    setDeleteSubmitting(true);
    try {
      await forgeService.deleteForge(deleteForge.client_id, deleteForge.forge_id);
      notify.success(t('forgeDashboard.forges.deleteSuccess', 'Forge deleted'));
      setDeleteForge(null);
      setDeleteInput('');
      fetchAll();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.forges.deleteError', 'Failed to delete forge')));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Layers className="h-4 w-4" />
          <span>{t('forgeDashboard.forges.summary', '{{count}} forges', { count: forges.length })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('global.refresh', 'Refresh')}
          </Button>
          <Button
            onClick={() => {
              setCreateForm(defaultCreate());
              setCreateOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('forgeDashboard.forges.new', 'New forge')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive">{error}</div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <TableSkeleton rows={5} cols={6} />
          ) : forges.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {t('forgeDashboard.forges.empty', 'No forges yet')}
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.forges.name', 'Name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.forges.client', 'Client')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('global.status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.forges.apps', 'Apps')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.forges.createdAt', 'Created')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {forges.map((f) => {
                  const client = clientById.get(f.client_id);
                  const appsCount = (f.system_apps?.length || 0) + (f.custom_apps?.length || 0);
                  return (
                    <tr key={f.forge_id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-foreground">{f.display_name || f.name}</div>
                        {f.display_name && f.display_name !== f.name && (
                          <div className="text-xs text-muted-foreground truncate max-w-xs" title={f.name}>
                            {f.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{client?.name || f.client_id}</td>
                      <td className="px-4 py-3">
                        {f.status && <Badge variant={getStatusBadgeVariant(f.status)}>{f.status}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{appsCount}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{formatDate(f.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/forges/${f.forge_id}`)}
                            className="gap-1.5"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {t('global.view', 'View')}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openEdit(f)} className="gap-1.5">
                            <Pencil className="h-3.5 w-3.5" />
                            {t('global.edit', 'Edit')}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => { setDeleteForge(f); setDeleteInput(''); }}
                            className="gap-1.5"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {t('global.delete', 'Delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create modal */}
      <Modal
        open={createOpen}
        title={t('forgeDashboard.forges.newTitle', 'Create forge')}
        description={t(
          'forgeDashboard.forges.newDescription',
          'Select the client that will own this forge, then provide its identity and defaults.'
        )}
        onClose={() => {
          if (createSubmitting) return;
          setCreateOpen(false);
        }}
        hideDefaultFooter
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createSubmitting || !canSubmitCreate}
            >
              {createSubmitting ? t('global.saving', 'Saving…') : t('global.create', 'Create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {isCreating && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-sm font-medium mb-1">
                {hasCreateError
                  ? t('forgeDashboard.forges.progress.failed', 'Forge creation failed')
                  : t('forgeDashboard.forges.progress.inProgress', 'Forge creation in progress…')}
              </p>
              <ProgressStepper
                steps={createSteps}
                currentStep={createStep}
                hasError={hasCreateError}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.forges.client', 'Client')}
            </label>
            <select
              className={inputCls}
              value={createForm.client_id}
              onChange={(e) => setCreateForm({ ...createForm, client_id: e.target.value })}
              disabled={createSubmitting}
            >
              <option value="">
                {t('forgeDashboard.forges.selectClient', '-- Select a client --')}
              </option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forgeDashboard.forges.name', 'Name')}
              </label>
              <input
                className={`${inputCls} ${nameInvalid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="my-forge"
                aria-invalid={nameInvalid || undefined}
                disabled={createSubmitting}
              />
              {nameInvalid ? (
                <p className="text-xs text-destructive mt-1">
                  {t(
                    'forgeDashboard.forges.nameInvalid',
                    'Use lowercase letters, digits and hyphens only (2–63 chars, must start/end with a letter or digit).'
                  )}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('forgeDashboard.forges.nameHint', 'lowercase, digits, hyphens (2–63)')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forgeDashboard.forges.displayName', 'Display name')}
              </label>
              <input
                className={inputCls}
                value={createForm.display_name}
                onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                disabled={createSubmitting}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.forges.description', 'Description')}
            </label>
            <textarea
              className={inputCls}
              rows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              disabled={createSubmitting}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forgeDashboard.forges.validationStrategy', 'Validation strategy')}
              </label>
              <select
                className={inputCls}
                value={createForm.validation_strategy}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    validation_strategy: e.target.value as 'strict' | 'permissive',
                  })
                }
                disabled={createSubmitting}
              >
                <option value="strict">strict</option>
                <option value="permissive">permissive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forgeDashboard.forges.registryStorage', 'Registry storage')}
              </label>
              <input
                className={inputCls}
                value={createForm.registry_storage_size}
                onChange={(e) => setCreateForm({ ...createForm, registry_storage_size: e.target.value })}
                disabled={createSubmitting}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="auto_rollback"
              type="checkbox"
              checked={createForm.auto_rollback_enabled}
              onChange={(e) =>
                setCreateForm({ ...createForm, auto_rollback_enabled: e.target.checked })
              }
              disabled={createSubmitting}
            />
            <label htmlFor="auto_rollback" className="text-sm">
              {t('forgeDashboard.forges.autoRollback', 'Enable auto-rollback')}
            </label>
          </div>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editForge}
        title={t('forgeDashboard.forges.editTitle', 'Edit forge')}
        onClose={() => setEditForge(null)}
        hideDefaultFooter
        footer={
          <>
            <Button variant="outline" onClick={() => setEditForge(null)} disabled={editSubmitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleUpdate} disabled={editSubmitting}>
              {editSubmitting ? t('global.saving', 'Saving…') : t('global.save', 'Save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.forges.displayName', 'Display name')}
            </label>
            <input
              className={inputCls}
              value={editForm.display_name}
              onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.forges.validationStrategy', 'Validation strategy')}
            </label>
            <select
              className={inputCls}
              value={editForm.validation_strategy}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  validation_strategy: e.target.value as 'strict' | 'permissive',
                })
              }
            >
              <option value="strict">strict</option>
              <option value="permissive">permissive</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="edit_auto_rollback"
              type="checkbox"
              checked={editForm.auto_rollback_enabled}
              onChange={(e) => setEditForm({ ...editForm, auto_rollback_enabled: e.target.checked })}
            />
            <label htmlFor="edit_auto_rollback" className="text-sm">
              {t('forgeDashboard.forges.autoRollback', 'Enable auto-rollback')}
            </label>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={!!deleteForge}
        title={t('forgeDashboard.forges.deleteTitle', 'Delete forge?')}
        message={t(
          'forgeDashboard.forges.deleteMessage',
          'This will permanently delete forge "{{name}}" and all its applications in Forge. This cannot be undone.',
          { name: deleteForge?.display_name || deleteForge?.name || '' }
        )}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        loading={deleteSubmitting}
        requireTextMatch={
          deleteForge
            ? { text: deleteForge.name, label: t('forgeDashboard.confirmTypeName', 'Type the name to confirm') }
            : undefined
        }
        inputValue={deleteInput}
        onInputChange={setDeleteInput}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteForge(null); setDeleteInput(''); }}
      />
    </div>
  );
};

export default ForgesTab;
